import { createColumnHelper } from '@tanstack/react-table';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';
import type { FilterField } from '~/styles/tables/filterandSettingsStyles';
import { field } from './queryArgs';

type Schemas = components['schemas'];
export type WaitingStudent = Schemas['AdminWaitingStudent'];
export type GradeStudent = Schemas['AdminWaitingGradeStudent'];
export type RequestRow = Schemas['AdminAdoptionRequestRow'];
export type HopefulStudent = Schemas['AdminHopefulStudent'];
export type AdoptionRow = Schemas['AdminAdoptionHistoryRow'];
type WaitingOnAction = Schemas['WaitingOnAction'];

export const STUDY_MODES: Schemas['ProfileType'][] = [
  'WEB_STUDENT',
  'POSTAL_STUDENT',
  'EMAIL_STUDENT',
  'LITEAPP_STUDENT',
];
export const LANGUAGES: Schemas['Language'][] = ['EN', 'ES', 'FR', 'PT_BR'];
export const WAITING_ON: Schemas['AdminWaitingOnFilter'][] = [
  'need_grade_quiz',
  'need_unlock_course',
  'need_assign_certificate',
  'need_assign_series_certificate',
];
export const REQUEST_STATUSES: Schemas['AdoptionRequestStatus'][] = [
  'pending',
  'accepted',
  'declined',
  'awaiting_approval',
  'admin_rejected',
];

/** `Intl.DateTimeFormat` in the browser's locale; the console is en-only. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString();
}

// The enum values read as a label via a fixed key per value, so the admin
// strings stay greppable.
export function modeLabel(mode: Schemas['ProfileType']): string {
  return i18n.t(`admin.waiting.mode.${mode}`);
}

export function waitingOnLine(a: WaitingOnAction): string {
  switch (a.target) {
    case 'quiz':
      return i18n.t('admin.waiting.cell.gradeQuiz', { count: a.count });
    case 'course':
      return i18n.t('admin.waiting.cell.unlockCourse', { count: a.count });
    case 'certificate':
      return i18n.t('admin.waiting.cell.assignCertificate', { count: a.count });
    case 'series_certificate':
      return i18n.t('admin.waiting.cell.assignSeriesCertificate', {
        count: a.count,
      });
    default:
      return i18n.t('admin.waiting.cell.needIntroduction');
  }
}

function options<V extends string>(values: V[], label: (v: V) => string) {
  return values.map((value) => ({ value, label: label(value) }));
}

/** Filters shared by the Waiting and Grades tabs. */
export function studentFilterFields(withWaitingOn: boolean): FilterField[] {
  return [
    field('student_id', i18n.t('admin.common.id'), 'text'),
    field('first_name', i18n.t('admin.waiting.filter.firstName'), 'text'),
    field('last_name', i18n.t('admin.waiting.filter.lastName'), 'text'),
    field(
      'study_mode',
      i18n.t('admin.waiting.col.mode'),
      'multiselect',
      options(STUDY_MODES, modeLabel),
    ),
    field(
      'language',
      i18n.t('admin.common.language'),
      'multiselect',
      options(LANGUAGES, (l) => i18n.t(`admin.waiting.lang.${l}`)),
    ),
    field(
      'study_helper_id',
      i18n.t('admin.waiting.col.studyHelper'),
      'multiselect',
    ),
    ...(withWaitingOn
      ? [
          field(
            'waiting_on',
            i18n.t('admin.waiting.col.waitingOn'),
            'select',
            options(WAITING_ON, (w) => i18n.t(`admin.waiting.waitingOn.${w}`)),
          ),
        ]
      : []),
  ];
}

const waitingHelper = createColumnHelper<WaitingStudent>();
export const waitingColumns = [
  waitingHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  waitingHelper.accessor((r) => r.display_name, {
    id: 'name',
    header: () => i18n.t('admin.common.name'),
  }),
  waitingHelper.accessor((r) => modeLabel(r.study_mode), {
    id: 'mode',
    header: () => i18n.t('admin.waiting.col.mode'),
  }),
  waitingHelper.accessor((r) => r.language, {
    id: 'language',
    header: () => i18n.t('admin.common.language'),
  }),
  waitingHelper.accessor((r) => formatDate(r.last_active_at), {
    id: 'last_active',
    header: () => i18n.t('admin.waiting.col.lastActive'),
  }),
  waitingHelper.accessor((r) => r.study_helper?.display_name ?? '', {
    id: 'study_helper',
    header: () => i18n.t('admin.waiting.col.studyHelper'),
  }),
  waitingHelper.accessor((r) => r.helper_groups.map((g) => g.name).join(', '), {
    id: 'helper_groups',
    header: () => i18n.t('admin.waiting.col.helperGroups'),
  }),
  waitingHelper.display({
    id: 'waiting_on',
    header: () => i18n.t('admin.waiting.col.waitingOn'),
    cell: ({ row }) => (
      <>
        {row.original.waiting_on.map((a) => (
          <div key={`${a.target}`}>{waitingOnLine(a)}</div>
        ))}
      </>
    ),
  }),
];

const gradeHelper = createColumnHelper<GradeStudent>();
export const gradeColumns = [
  gradeHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  gradeHelper.accessor((r) => r.display_name, {
    id: 'name',
    header: () => i18n.t('admin.common.name'),
  }),
  gradeHelper.accessor((r) => modeLabel(r.study_mode), {
    id: 'mode',
    header: () => i18n.t('admin.waiting.col.mode'),
  }),
  gradeHelper.accessor((r) => r.language, {
    id: 'language',
    header: () => i18n.t('admin.common.language'),
  }),
  gradeHelper.accessor((r) => r.study_helper?.display_name ?? '', {
    id: 'study_helper',
    header: () => i18n.t('admin.waiting.col.studyHelper'),
  }),
  gradeHelper.accessor((r) => r.ungraded_quiz ?? '', {
    id: 'ungraded_quiz',
    header: () => i18n.t('admin.waiting.col.ungradedQuizName'),
  }),
  gradeHelper.accessor((r) => formatDate(r.ungraded_since), {
    id: 'ungraded_since',
    header: () => i18n.t('admin.waiting.col.ungradedQuizDate'),
  }),
];

export function reasonText(r: RequestRow): string {
  if (!r.reason) return '';
  return r.reason === 'other' && r.reason_other
    ? `${i18n.t('admin.waiting.reason.other')}: ${r.reason_other}`
    : i18n.t(`admin.waiting.reason.${r.reason}`);
}

const requestHelper = createColumnHelper<RequestRow>();
export function requestColumns(kind: 'switch' | 'adoption' | 'history') {
  return [
    requestHelper.accessor((r) => r.student.id, {
      id: 'id',
      header: () => i18n.t('admin.common.id'),
    }),
    requestHelper.accessor((r) => r.student.display_name, {
      id: 'name',
      header: () => i18n.t('admin.common.name'),
    }),
    requestHelper.accessor((r) => formatDate(r.student_registered_at), {
      id: 'registered',
      header: () => i18n.t('admin.waiting.col.registered'),
    }),
    requestHelper.accessor((r) => r.current_helper?.display_name ?? '', {
      id: 'current_helper',
      header: () => i18n.t('admin.waiting.col.currentHelper'),
    }),
    requestHelper.accessor((r) => r.requested_helper.display_name, {
      id: 'requested_helper',
      header: () => i18n.t('admin.waiting.col.requestedHelper'),
    }),
    ...(kind === 'switch'
      ? [
          requestHelper.accessor((r) => reasonText(r), {
            id: 'reason',
            header: () => i18n.t('admin.waiting.col.reason'),
          }),
          requestHelper.accessor((r) => r.ungraded_quizzes.join(', '), {
            id: 'ungraded_quizzes',
            header: () => i18n.t('admin.waiting.col.ungradedQuizzes'),
          }),
        ]
      : []),
    requestHelper.accessor((r) => formatDate(r.requested_at), {
      id: 'requested_at',
      header: () => i18n.t('admin.waiting.col.requestedAt'),
    }),
    ...(kind === 'history'
      ? [
          requestHelper.accessor(
            (r) => i18n.t(`admin.waiting.status.${r.status}`),
            {
              id: 'status',
              header: () => i18n.t('admin.common.status'),
            },
          ),
          requestHelper.accessor((r) => formatDate(r.status_changed_at), {
            id: 'status_changed_at',
            header: () => i18n.t('admin.waiting.col.statusChangedAt'),
          }),
        ]
      : []),
  ];
}

const hopefulHelper = createColumnHelper<HopefulStudent>();
export const hopefulColumns = [
  hopefulHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  hopefulHelper.accessor((r) => r.display_name, {
    id: 'name',
    header: () => i18n.t('admin.common.name'),
  }),
  hopefulHelper.accessor((r) => r.language, {
    id: 'language',
    header: () => i18n.t('admin.common.language'),
  }),
  hopefulHelper.accessor(
    (r) => (r.intro_progress === null ? '' : `${r.intro_progress}%`),
    { id: 'intro', header: () => i18n.t('admin.waiting.col.intro') },
  ),
  hopefulHelper.accessor((r) => formatDate(r.registered_at), {
    id: 'registered',
    header: () => i18n.t('admin.waiting.col.registered'),
  }),
  hopefulHelper.accessor((r) => r.matched_campaign?.name ?? '', {
    id: 'matched_campaign',
    header: () => i18n.t('admin.waiting.col.matchedCampaign'),
  }),
  hopefulHelper.accessor(
    (r) => r.matched_groups.map((g) => g.name).join(', '),
    {
      id: 'matched_targets',
      header: () => i18n.t('admin.waiting.col.matchedTargets'),
    },
  ),
];

const adoptionHelper = createColumnHelper<AdoptionRow>();
export const adoptionColumns = [
  adoptionHelper.accessor((r) => r.student.id, {
    id: 'id',
    header: () => i18n.t('admin.common.id'),
  }),
  adoptionHelper.accessor((r) => r.student.display_name, {
    id: 'name',
    header: () => i18n.t('admin.common.name'),
  }),
  adoptionHelper.accessor((r) => r.study_helper.display_name, {
    id: 'study_helper',
    header: () => i18n.t('admin.waiting.col.studyHelper'),
  }),
  adoptionHelper.accessor((r) => r.assigned_by?.display_name ?? '', {
    id: 'assigned_by',
    header: () => i18n.t('admin.waiting.col.assignedBy'),
  }),
  adoptionHelper.accessor((r) => r.campaign?.name ?? '', {
    id: 'campaign',
    header: () => i18n.t('admin.waiting.col.matchedCampaign'),
  }),
  adoptionHelper.accessor((r) => formatDate(r.adopted_at), {
    id: 'adopted_at',
    header: () => i18n.t('admin.waiting.col.adoptedAt'),
  }),
  adoptionHelper.accessor((r) => formatDate(r.released_at), {
    id: 'released_at',
    header: () => i18n.t('admin.waiting.col.releasedAt'),
  }),
  adoptionHelper.accessor((r) => r.released_by?.display_name ?? '', {
    id: 'released_by',
    header: () => i18n.t('admin.waiting.col.releasedBy'),
  }),
];
